import { MouseEvent, MouseEventHandler, useEffect, useState } from 'react';

/**
 * Arranges for a button to be disabled while its async click action runs.
 *
 * The hook returns an async onClick plus pending, timeLeft and running properties.
 * This hook should be called with onClick (mandatory) and delay time.
 *
 * @returns {Object}
 * @param {function} onClick the asynchronous button action (mandatory)
 * @param {number} [delay]     an optional cooldown (in seconds), started once the action succeeds. Used to delay the
 * re-enabling of the button so that even if the asynchronous action is quick it cannot be re-invoked for an additional
 * period. This is handy for throttling/debouncing functions.
 * @param {boolean} [disableInitially] start in the cooldown period, as if the action had just run
 * */
export function useAsyncButton(
  onClick: MouseEventHandler<HTMLButtonElement | HTMLElement>,
  delay = 0,
  disableInitially = false
) {
  const delayMs = (Number.isFinite(delay) ? delay : 0) * 1000;
  const [pending, setPending] = useState(false);
  const [cooldownEnd, setCooldownEnd] = useState(() => (disableInitially ? Date.now() + delayMs : 0));
  const [now, setNow] = useState(Date.now);

  // tick once a second until the cooldown has elapsed; timeLeft is derived from the deadline so it can't drift
  useEffect(() => {
    if (now >= cooldownEnd) return;
    const timeout = setTimeout(() => setNow(Date.now()), Math.min(1000, cooldownEnd - now));
    return () => clearTimeout(timeout);
  }, [now, cooldownEnd]);

  const timeLeft = Math.max(0, Math.ceil((cooldownEnd - now) / 1000));

  return {
    onClick: async (e: MouseEvent<HTMLButtonElement | HTMLElement>) => {
      setPending(true);
      try {
        await onClick(e); // NOSONAR
        const succeededAt = Date.now();
        setNow(succeededAt);
        setCooldownEnd(succeededAt + delayMs);
      } finally {
        setPending(false);
      }
    },
    pending,
    timeLeft,
    running: pending || timeLeft > 0
  };
}

export default useAsyncButton;
